import { useEffect, useRef, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useSiteConfig } from '@/context/SiteConfigContext';
import { cn } from '@/lib/utils';
import { httpsUrl } from '@/lib/safeUrl';

/** Google's own badge artwork, served from the same CDN as the site's images. */
const HOVER_GRACE_MS = 200;

const PLAY_BADGE =
  'https://res.cloudinary.com/dvtqocatk/image/upload/v1783618337/GetItOnGooglePlay_Badge_Web_color_English_prmgb6.png';

function QrTile({ url, label, sub }: { url: string; label: string; sub: string }) {
  return (
    <div className="flex flex-col items-center text-center">
      <div className="rounded-lg border bg-white p-2">
        <QRCodeSVG value={url} size={120} level="M" aria-label={`QR code for ${label}`} />
      </div>
      <p className="mt-2 text-sm font-medium text-foreground">{label}</p>
      <p className="text-xs text-muted-foreground">{sub}</p>
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-1 text-xs text-primary hover:underline"
      >
        Open in Play Store
      </a>
    </div>
  );
}

/**
 * The Play Store badge in the navbar. Hovering shows QR codes for both apps so
 * someone at a desk can point their phone at the screen; clicking does the same
 * for keyboards and touch. Both apps are offered regardless of who is signed
 * in, since a clinic owner and a patient can be the same person.
 */
export function AppDownloadBadge({ className }: { className?: string }) {
  const { user_app_android_url, clinic_app_android_url } = useSiteConfig();
  const [open, setOpen] = useState(false);
  // The popover floats a few pixels below the badge. Closing on the instant
  // the pointer crosses that gap would snatch the QR codes away, so leaving
  // starts a short timer that entering either part cancels.
  const closeTimer = useRef<number | null>(null);
  // Radix moves focus into the popover on open and back to the badge on close.
  // That is right for a keyboard, and wrong for a hover or a click: the badge
  // is left wearing a focus ring nobody asked for. Remember how it was opened.
  const viaKeyboard = useRef(false);
  const keepFocusForPointer = (event: Event) => {
    if (!viaKeyboard.current) event.preventDefault();
  };
  const openNow = () => {
    if (closeTimer.current) window.clearTimeout(closeTimer.current);
    closeTimer.current = null;
    setOpen(true);
  };
  const closeSoon = () => {
    if (closeTimer.current) window.clearTimeout(closeTimer.current);
    closeTimer.current = window.setTimeout(() => setOpen(false), HOVER_GRACE_MS);
  };
  // A click, Escape or outside tap goes through Radix, not the hover paths,
  // so it must cancel a pending hover close too, or that close lands on the
  // popover the person just opened on purpose.
  const setOpenNow = (next: boolean) => {
    if (closeTimer.current) window.clearTimeout(closeTimer.current);
    closeTimer.current = null;
    setOpen(next);
  };
  useEffect(() => () => {
    if (closeTimer.current) window.clearTimeout(closeTimer.current);
  }, []);
  const patient = httpsUrl(user_app_android_url) ?? '';
  const clinic = httpsUrl(clinic_app_android_url) ?? '';
  if (!patient && !clinic) return null;

  return (
    <Popover open={open} onOpenChange={setOpenNow}>
      <div
        className={cn('inline-flex', className)}
        onMouseEnter={() => {
          viaKeyboard.current = false;
          openNow();
        }}
        onMouseLeave={closeSoon}
      >
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label="Get the Recovry app on Google Play"
            onPointerDown={() => {
              viaKeyboard.current = false;
            }}
            onKeyDown={() => {
              viaKeyboard.current = true;
            }}
            className="rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            <img
              src={PLAY_BADGE}
              alt="Get it on Google Play"
              width={108}
              height={32}
              className="h-8 w-auto"
            />
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="end"
          sideOffset={6}
          className="w-auto p-4"
          onMouseEnter={openNow}
          onMouseLeave={closeSoon}
          onOpenAutoFocus={keepFocusForPointer}
          onCloseAutoFocus={keepFocusForPointer}
        >
          <p className="text-sm font-semibold text-foreground">Scan to install</p>
          <div className="mt-3 flex gap-6">
            {patient && <QrTile url={patient} label="Recovry" sub="for patients" />}
            {clinic && <QrTile url={clinic} label="Recovry Practice" sub="for clinics" />}
          </div>
        </PopoverContent>
      </div>
    </Popover>
  );
}
